import type { DashboardMetricItemData } from '../../../../types/dashboardMetrics.types'

const FOOTER_VS_YESTERDAY = '6% ($800) vs. yesterday'
const FOOTER_MORE = '6% ($800) more vs. yesterday'

/** Today's forecast section metrics. Replace with API call when ready. */
export function getHuddleTodayForecastMetrics(): DashboardMetricItemData[] {
  return [
    {
      id: 'expected-production',
      leftLabel: 'Expected Production',
      rightLabel: 'Goal 19K',
      value: '$9.4K',
      footer: FOOTER_VS_YESTERDAY,
      footerTrend: 'up',
    },
    {
      id: 'scheduled-production',
      leftLabel: 'Scheduled production',
      rightLabel: 'Goal 2.3K',
      value: '$2.1K',
      footer: FOOTER_VS_YESTERDAY,
      footerTrend: 'down',
    },
    {
      id: 'pending-treatments',
      leftLabel: 'Pending treatments',
      rightLabel: 'Goal 2.3K',
      value: '$8.4K',
      footer: FOOTER_MORE,
      footerTrend: 'up',
    },
    {
      id: 'ar-due',
      leftLabel: 'AR Due',
      value: '$8.4K',
      footer: '$1.2K over 90 days',
    },
  ]
}

/** Patients section metrics. Replace with API call when ready. */
export function getHuddleTodayPatientsMetrics(): DashboardMetricItemData[] {
  return [
    {
      id: 'scheduled-patients',
      leftLabel: 'Scheduled patients today',
      rightLabel: 'Goal 20',
      value: '32',
      footer: '6% (20) vs. yesterday',
      footerTrend: 'up',
    },
    {
      id: 'new-patients',
      leftLabel: 'New patients',
      rightLabel: 'Goal 4',
      value: '12',
      footer: '6% (2) vs. yesterday',
      footerTrend: 'up',
    },
    {
      id: 'unscheduled-patients',
      leftLabel: 'Unscheduled patients',
      value: '9',
      valueTone: 'danger',
      footer: '6% (3) vs. yesterday',
      footerTrend: 'down',
    },
    {
      id: 'follow-up-needed',
      leftLabel: 'Follow-up needed',
      value: '5',
      valueTone: 'danger',
      footer: '6% (1) vs. yesterday',
      footerTrend: 'down',
    },
  ]
}

/** Provider performance breakdown — 2×2 grid metrics. */
export function getHuddleProviderPerformanceGrid(): DashboardMetricItemData[] {
  return [
    {
      id: 'same-day-tx',
      leftLabel: 'Same day treatment',
      value: '$3.2K',
      footer: FOOTER_MORE,
      footerTrend: 'up',
    },
    {
      id: 'accepted-tx',
      leftLabel: 'Accepted treatment',
      value: '$2.4K',
      footer: FOOTER_MORE,
      footerTrend: 'up',
    },
    {
      id: 'perio-rate',
      leftLabel: 'Perio rate',
      value: '23.4%',
      footer: FOOTER_MORE,
      footerTrend: 'up',
    },
    {
      id: 'hygiene-util',
      leftLabel: 'Hygiene Utilization %',
      value: '23.4%',
      footer: FOOTER_MORE,
      footerTrend: 'up',
    },
  ]
}

const FOOTER_MORE_YD = '6% ($800) more vs. yd'

/** Provider performance breakdown detail page summary metrics. */
export function getHuddleProviderPerformanceDetailMetrics(): DashboardMetricItemData[] {
  return [
    {
      id: 'same-day-tx',
      leftLabel: 'Same day treatment',
      value: '$3.2K',
      footer: FOOTER_MORE_YD,
      footerTrend: 'up',
    },
    {
      id: 'accepted-tx',
      leftLabel: 'Accepted treatment',
      value: '$2.4K',
      footer: FOOTER_MORE_YD,
      footerTrend: 'up',
    },
    {
      id: 'hourly-production',
      leftLabel: 'Hourly production',
      value: '$1.7K',
      footer: FOOTER_MORE_YD,
      footerTrend: 'up',
    },
    {
      id: 'perio-rate',
      leftLabel: 'Perio rate',
      value: '23.4%',
      footer: FOOTER_MORE_YD,
      footerTrend: 'up',
    },
    {
      id: 'hygiene-utilization',
      leftLabel: 'Hygiene utilization',
      value: '23.4%',
      footer: FOOTER_MORE_YD,
      footerTrend: 'up',
    },
    {
      id: 'collections',
      leftLabel: 'Collections',
      value: '$8.4K',
      footer: FOOTER_MORE_YD,
      footerTrend: 'up',
    },
  ]
}

/** Yesterday's Performance card (Today, row of 3). */
export function getHuddleYesterdaysPerformanceMetrics(): DashboardMetricItemData[] {
  return [
    {
      id: 'yp-production-recap',
      leftLabel: 'Production recap',
      value: '$3.2K',
      footer: FOOTER_MORE,
      footerTrend: 'up',
    },
    {
      id: 'yp-collections',
      leftLabel: 'Collections',
      value: '$2.4K',
      footer: FOOTER_MORE,
      footerTrend: 'up',
    },
    {
      id: 'yp-scheduled-appt',
      leftLabel: 'Scheduled appointments',
      value: '$1.7K',
      footer: FOOTER_MORE,
      footerTrend: 'up',
    },
    {
      id: 'yp-production-goal',
      leftLabel: 'Production',
      rightLabel: 'Goal 9K',
      value: '$8.4K',
      footer: FOOTER_MORE,
      footerTrend: 'up',
    },
  ]
}

/** Visible name rows before the list scrolls (Figma 5459-113923). */
export const HUDDLE_EVENTS_LIST_VISIBLE_ROWS = 5

export type HuddleEventEmptyState = {
  heading: string
  description: string
}

/** Empty column copy (Figma 5459-113994). Shown when an event list has no rows. */
export const HUDDLE_EVENT_EMPTY_BY_ID: Record<string, HuddleEventEmptyState> = {
  'events-birthdays': {
    heading: 'No birthdays',
    description: 'No birthdays are to be shown as of the moment.',
  },
  'events-anniversaries': {
    heading: 'No anniversaries',
    description: 'No anniversaries are to be shown as of the moment.',
  },
  'events-achievements': {
    heading: 'No achievements',
    description: 'No achievements are to be shown as of the moment.',
  },
  'events-greeting': {
    heading: 'No reminders',
    description: 'No greeting reminders are to be shown as of the moment.',
  },
}

export function getHuddleEventEmptyState(
  eventId: string,
): HuddleEventEmptyState {
  return (
    HUDDLE_EVENT_EMPTY_BY_ID[eventId] ?? {
      heading: 'No events',
      description: 'No events are to be shown as of the moment.',
    }
  )
}

export type HuddleLeaderboardRow = {
  id: string
  rank: string
  name: string
  role: string
  productionCollected: string
}

export function getHuddleLeaderboardRows(): HuddleLeaderboardRow[] {
  return [
    {
      id: '1',
      rank: '1st',
      name: 'John Smith',
      role: 'Dentist',
      productionCollected: '$2.1K',
    },
    {
      id: '2',
      rank: '2nd',
      name: 'Jane Doe',
      role: 'Hygienist',
      productionCollected: '$2.0K',
    },
    {
      id: '3',
      rank: '3rd',
      name: 'Emily Johnson',
      role: 'Dentist',
      productionCollected: '$1.9K',
    },
    {
      id: '4',
      rank: '4th',
      name: 'Michael Brown',
      role: 'Orthodontist',
      productionCollected: '$1.8K',
    },
    {
      id: '5',
      rank: '5th',
      name: 'David Martinez',
      role: 'Oral Surgeon',
      productionCollected: '$1.3K',
    },
    {
      id: '6',
      rank: '6th',
      name: 'Sarah Wilson',
      role: 'Endodontist',
      productionCollected: '$1.1K',
    },
  ]
}

/** Yesterday: provider actuals detail summary metrics. */
export function getHuddleYesterdayProviderDetailMetrics(): DashboardMetricItemData[] {
  return [
    {
      id: 'y-prov-total-production',
      leftLabel: 'Total production',
      value: '$16.2K',
      footer: '91% of goal',
    },
    {
      id: 'y-prov-total-collections',
      leftLabel: 'Total collections',
      value: '$14.1K',
      footer: '93% of goal',
    },
    {
      id: 'y-prov-top-performer',
      leftLabel: 'Top performer',
      value: 'John Smith',
      footer: '$6.2K production',
    },
    {
      id: 'y-prov-patients-seen',
      leftLabel: 'Patients seen',
      rightLabel: 'Goal 28',
      value: '26',
      footer: '4 providers active',
    },
  ]
}

/** Leaderboard detail page summary metrics. */
export function getHuddleLeaderboardDetailMetrics(): DashboardMetricItemData[] {
  return [
    {
      id: 'lb-total-production',
      leftLabel: 'Total production collected',
      value: '$10.2K',
      footer: '8% more vs. last week',
      footerTrend: 'up',
    },
    {
      id: 'lb-top-performer',
      leftLabel: 'Top performer',
      value: 'John Smith',
      footer: '$2.1K collected today',
    },
    {
      id: 'lb-practice-average',
      leftLabel: 'Practice average',
      rightLabel: 'per provider',
      value: '$1.7K',
      footer: '5% more vs. last week',
      footerTrend: 'up',
    },
    {
      id: 'lb-overall-goal',
      leftLabel: 'Overall goal attainment',
      rightLabel: 'Goal 100%',
      value: '92%',
      footer: '4 of 6 providers at or above goal',
    },
  ]
}

/** Yesterday: actuals — production & patients (section 1). Replace with API when ready. */
export function getHuddleYesterdayActualsPart1(): DashboardMetricItemData[] {
  return [
    {
      id: 'y-actual-production',
      leftLabel: 'Production (actual)',
      rightLabel: 'Goal 19K',
      value: '$17.2K',
      footer: '91% of goal',
    },
    {
      id: 'y-actual-collections',
      leftLabel: 'Collections (actual)',
      rightLabel: 'Goal 16K',
      value: '$14.8K',
      footer: '93% of goal',
    },
    {
      id: 'y-patients-seen',
      leftLabel: 'Patients seen',
      rightLabel: 'Goal 28',
      value: '26',
      footer: '2 no-shows',
    },
    {
      id: 'y-new-patients',
      leftLabel: 'New patients',
      value: '3',
    },
  ]
}

/** Yesterday: actuals — schedule & adjustments (section 2). Replace with API when ready. */
export function getHuddleYesterdayActualsPart2(): DashboardMetricItemData[] {
  return [
    { id: 'y-no-shows', leftLabel: 'No-shows', value: '2' },
    { id: 'y-cancellations', leftLabel: 'Cancellations', value: '1' },
    { id: 'y-broken-appts', leftLabel: 'Broken appointments', value: '0' },
    {
      id: 'y-chair-util',
      leftLabel: 'Chair utilization',
      value: '85%',
      rightLabel: 'Goal 90%',
    },
    { id: 'y-adjustments', leftLabel: 'Adjustments', value: '$420' },
  ]
}

/** Yesterday tab: Performance vs goal KPIs. */
export function getHuddleYesterdayPerformanceMetrics(): DashboardMetricItemData[] {
  return [
    {
      id: 'y-perf-production',
      leftLabel: 'Production',
      value: '91%',
      valueOver: 'of goal',
      footer: 'Below target by $1.8K',
    },
    {
      id: 'y-perf-collections',
      leftLabel: 'Collections',
      value: '93%',
      valueOver: 'of goal',
      footer: 'Below target by $1.2K',
    },
  ]
}

/** Yesterday: provider actuals (same shape as leaderboard). */
export function getHuddleYesterdayProviderRows(): HuddleLeaderboardRow[] {
  return [
    {
      id: '1',
      rank: '1st',
      name: 'John Smith',
      role: 'Dentist',
      productionCollected: '$6.2K',
    },
    {
      id: '2',
      rank: '2nd',
      name: 'Jane Doe',
      role: 'Hygienist',
      productionCollected: '$4.1K',
    },
    {
      id: '3',
      rank: '3rd',
      name: 'Emily Johnson',
      role: 'Dentist',
      productionCollected: '$3.8K',
    },
    {
      id: '4',
      rank: '4th',
      name: 'Michael Brown',
      role: 'Orthodontist',
      productionCollected: '$2.1K',
    },
  ]
}

/** Tomorrow: forecast (same structure as Today's forecast). */
export function getHuddleTomorrowForecastMetrics(): DashboardMetricItemData[] {
  return [
    {
      id: 'tm-expected',
      leftLabel: 'Expected Production',
      rightLabel: 'Goal 19K',
      value: '$9.2K',
    },
    {
      id: 'tm-scheduled',
      leftLabel: 'Scheduled production',
      rightLabel: 'Goal 2.3K',
      value: '$2.4K',
    },
    {
      id: 'tm-pending',
      leftLabel: 'Pending treatments',
      rightLabel: 'Goal 2.3K',
      value: '$7.8K',
    },
    { id: 'tm-ar', leftLabel: 'AR Due', value: '$8.1K' },
  ]
}
